import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { IChatbotNavAction } from "api/chatbot/chatbot.type";
import type { PageSuggestion } from "components/DashboardChatbot/pageContext";

export type ChatStatus = "streaming" | "done" | "canceled" | "error" | "interrupted";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  status: ChatStatus;
  sessionId: string | null;
  isIntro?: boolean;
  suggestions?: PageSuggestion[];
  /**
   * Destinations the BE resolved from the capability graph. Rendered as one-tap navigation
   * buttons so "where do I do X" ends with the user ON the page instead of reading a path
   * out of prose (REF-6112).
   */
  actions?: IChatbotNavAction[];
}

/**
 * The conversation lives here — NOT in DashboardChatbot's local state — because the panel is
 * rendered inside `Layout`, which every route re-instantiates (`Router.tsx::getRoute`). A local
 * `useState` was therefore destroyed on every navigation, so an answer that told the user where
 * to go was gone by the time they got there (REF-6217 T0.1).
 *
 * The in-flight turn is lifted too, as of P1-1: `activeSessionId` + `lastChunkIndex`. They used to
 * be component-local refs, and the note here used to say restoring them after a remount would
 * resume chunk delivery into a bubble whose HTTP request the unmount had already aborted. That
 * objection no longer holds, twice over: the unmount no longer aborts, and `/v1/chatbot/ask` is
 * only a kickoff that returns immediately — the BE task is detached, so aborting the HTTP request
 * never stopped generation anyway. Keeping them local is what made a cross-layout navigation
 * (`Layout` → `AcademyLayout`) destroy a healthy answer the server went on to deliver.
 *
 * Only the canceled-session set and the streaming timers stay component-local: both are about
 * *this* mount's obligations, not the conversation.
 *
 * Every write below is keyed by session id, and the WS effect drops frames whose session isn't the
 * store's active one, so a stale turn can never write into a newer one.
 *
 * Not persisted across a page reload either (memory only): a hard refresh starts a new
 * conversation, same as today. Logout is a full `window.location` load, so it clears this for
 * free — see `router/Router.tsx` `detectPermissionDenied`.
 */
const findStreamingAssistant = (messages: ChatMessage[], sessionId: string) => messages.find((m) => m.sessionId === sessionId && m.role === "assistant" && m.status === "streaming");

export const chatbotSlice = createSlice({
  name: "chatbot",
  initialState: {
    messages: [] as ChatMessage[],
    /** Session id of the turn currently streaming, or null when the panel is idle. */
    activeSessionId: null as string | null,
    /** Highest `chunk_index` accepted for `activeSessionId`. -1 before the first chunk. */
    lastChunkIndex: -1,
  },
  reducers: {
    /** Replace the whole conversation — intro build and "New chat". */
    setChatbotMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
      state.activeSessionId = null;
      state.lastChunkIndex = -1;
    },
    /**
     * A new turn owns the stream from here. Survives a remount, which is the whole point: the
     * panel is re-instantiated by every layout change and must not conclude that the turn died.
     */
    startChatbotTurn: (state, action: PayloadAction<{ sessionId: string }>) => {
      state.activeSessionId = action.payload.sessionId;
      state.lastChunkIndex = -1;
    },
    /** Monotonic guard against out-of-order / replayed chunk frames. */
    noteChatbotChunkIndex: (state, action: PayloadAction<{ sessionId: string; chunkIndex: number }>) => {
      if (state.activeSessionId !== action.payload.sessionId) return;
      state.lastChunkIndex = action.payload.chunkIndex;
    },
    /** Append the user question + its empty streaming assistant bubble. */
    addChatbotMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages.push(...action.payload);
    },
    /** A `chatbot_chunk` frame with `status: "streaming"`. */
    appendChatbotChunk: (state, action: PayloadAction<{ sessionId: string; text: string }>) => {
      const target = findStreamingAssistant(state.messages, action.payload.sessionId);
      if (!target) return;

      target.text += action.payload.text;
    },
    /**
     * Move the in-flight assistant bubble of a session to a terminal status. `appendText` is the
     * trailing chunk carried by the `done` frame; `text` replaces the body (error frames).
     */
    finishChatbotMessage: (
      state,
      action: PayloadAction<{
        sessionId: string;
        status: Exclude<ChatStatus, "streaming">;
        appendText?: string;
        text?: string;
        actions?: IChatbotNavAction[];
      }>
    ) => {
      const { sessionId, status, appendText, text, actions } = action.payload;
      // Release the stream slot even when the bubble is already terminal (a late `done` after the
      // watchdog gave up, say) — otherwise the panel stays wedged in its streaming state.
      if (state.activeSessionId === sessionId) {
        state.activeSessionId = null;
        state.lastChunkIndex = -1;
      }
      const target = findStreamingAssistant(state.messages, sessionId);
      if (!target) return;

      if (typeof text === "string") target.text = text;
      if (appendText) target.text += appendText;
      target.status = status;
      if (actions) target.actions = actions;
    },
    /**
     * Fail one bubble by id — the HTTP send path, which knows the message it created but may not
     * be the current session by the time the request settles. `fallbackText` is used only when
     * chunks already landed and the existing body should stay.
     */
    failChatbotMessage: (state, action: PayloadAction<{ id: string; text?: string; fallbackText?: string }>) => {
      const { id, text, fallbackText } = action.payload;
      const target = state.messages.find((m) => m.id === id);
      if (target && target.sessionId && state.activeSessionId === target.sessionId) {
        state.activeSessionId = null;
        state.lastChunkIndex = -1;
      }
      if (!target) return;

      if (typeof text === "string") target.text = text;
      else if (fallbackText) target.text = target.text || fallbackText;
      target.status = "error";
    },
  },
});

export const { setChatbotMessages, addChatbotMessages, appendChatbotChunk, finishChatbotMessage, failChatbotMessage, startChatbotTurn, noteChatbotChunkIndex } = chatbotSlice.actions;

export default chatbotSlice.reducer;
